import React, { useState } from 'react';
import { 
  Workflow, Bell, Mail, Activity, Settings, Code, Cloud,
  Check, AlertTriangle, Info, ExternalLink, Eye,
  Copy, Save, RefreshCw, Download, Upload, Plus, Edit3,
  Trash2, Play, Pause, BarChart3, TrendingUp, Users,
  Database, Shield, Zap, Globe
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Workflow Templates Section
export const WorkflowTemplates = () => {
  const [workflows, setWorkflows] = useState([
    {
      id: '1',
      name: 'Onboarding Automation',
      description: 'Automated new hire onboarding process',
      category: 'HR',
      tenants: ['All'],
      status: 'active',
      usage: 245,
      lastModified: '2024-01-15'
    },
    {
      id: '2', 
      name: 'Candidate Screening Pipeline',
      description: 'AI-powered candidate evaluation workflow',
      category: 'Recruitment',
      tenants: ['Enterprise'],
      status: 'active',
      usage: 892,
      lastModified: '2024-01-18'
    },
    {
      id: '3',
      name: 'Interview Scheduling Bot',
      description: 'Automated interview coordination',
      category: 'Automation',
      tenants: ['Professional', 'Enterprise'],
      status: 'draft',
      usage: 67,
      lastModified: '2024-01-20'
    }
  ]);

  return (
    <div className="space-y-6">
      {/* Workflow Templates Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Global Workflow Templates</h2>
            <p className="text-sm text-gray-600">Manage reusable workflows across all tenants</p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="h-4 w-4" />
            <span>Create Template</span>
          </button>
        </div>

        {/* Template Categories */}
        <div className="flex space-x-2 mb-4">
          {['All', 'HR', 'Recruitment', 'Automation', 'Analytics'].map((category) => (
            <button
              key={category}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-blue-100 hover:text-blue-700"
            >
              {category}
            </button>
          ))}
        </div>

        {/* Workflows List */}
        <div className="space-y-3">
          {workflows.map((workflow) => (
            <div key={workflow.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-medium text-gray-900">{workflow.name}</h3>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {workflow.category}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      workflow.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {workflow.status}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2">{workflow.description}</p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>Available to: {workflow.tenants.join(', ')}</span>
                    <span>•</span>
                    <span>Used {workflow.usage} times</span>
                    <span>•</span>
                    <span>Modified {workflow.lastModified}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-blue-600">
                    <Eye className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-green-600">
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-purple-600">
                    <Copy className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Workflow Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3">
            <Workflow className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Total Templates</p>
              <p className="text-2xl font-bold text-gray-900">{workflows.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Total Executions</p>
              <p className="text-2xl font-bold text-gray-900">
                {workflows.reduce((sum, w) => sum + w.usage, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3">
            <Check className="h-8 w-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Active Templates</p>
              <p className="text-2xl font-bold text-gray-900">
                {workflows.filter(w => w.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Template Configuration */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Configuration</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Access Control</h4>
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Allow tenant customization</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Version control for templates</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">Require approval for changes</span>
              </label>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Distribution</h4>
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Auto-deploy updates</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">Plan-based template access</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Usage analytics tracking</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Monitoring & Alerts Section
export const MonitoringAlerts = () => {
  const [alerts, setAlerts] = useState([
    {
      id: '1',
      type: 'warning',
      message: 'Tenant "Acme Corp" approaching user limit (23/25)',
      timestamp: '2024-01-20 14:30',
      status: 'active'
    },
    {
      id: '2',
      type: 'error',
      message: 'Database connection issues detected',
      timestamp: '2024-01-20 13:15',
      status: 'resolved'
    },
    {
      id: '3',
      type: 'info',
      message: 'System maintenance scheduled for tonight',
      timestamp: '2024-01-20 10:00',
      status: 'active'
    }
  ]);

  const [monitoringConfig, setMonitoringConfig] = useState({
    systemHealth: true,
    tenantUsage: true,
    securityEvents: true,
    performanceMetrics: true,
    billingAlerts: true,
    integrationStatus: true
  });

  return (
    <div className="space-y-6">
      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm text-gray-600">System Status</p>
              <p className="font-semibold text-green-600">Operational</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3">
            <Activity className="h-6 w-6 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Uptime</p>
              <p className="font-semibold text-gray-900">99.97%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="font-semibold text-gray-900">1,247</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3">
            <BarChart3 className="h-6 w-6 text-orange-600" />
            <div>
              <p className="text-sm text-gray-600">Response Time</p>
              <p className="font-semibold text-gray-900">245ms</p>
            </div>
          </div>
        </div>
      </div>

      {/* Active Alerts */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Active Alerts</h2>
          <button className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200">
            View All
          </button>
        </div>

        <div className="space-y-3">
          {alerts.map((alert) => (
            <div key={alert.id} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg">
              <div className={`mt-0.5 w-2 h-2 rounded-full ${
                alert.type === 'error' ? 'bg-red-500' :
                alert.type === 'warning' ? 'bg-yellow-500' :
                'bg-blue-500'
              }`}></div>
              
              <div className="flex-1">
                <p className="text-sm text-gray-900">{alert.message}</p>
                <p className="text-xs text-gray-500 mt-1">{alert.timestamp}</p>
              </div>

              <span className={`px-2 py-1 text-xs rounded ${
                alert.status === 'active' 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {alert.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Monitoring Configuration */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monitoring Configuration</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Alert Categories</h4>
            <div className="space-y-3">
              {Object.entries(monitoringConfig).map(([key, enabled]) => (
                <label key={key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setMonitoringConfig({
                      ...monitoringConfig,
                      [key]: e.target.checked
                    })}
                    className="rounded"
                  />
                  <span className="text-sm capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Notification Channels</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Email Alerts</span>
                </div>
                <button className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded hover:bg-green-200">
                  Enabled
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Bell className="h-4 w-4 text-purple-600" />
                  <span className="text-sm">Slack Notifications</span>
                </div>
                <button className="px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded hover:bg-gray-200">
                  Configure
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Webhook</span>
                </div>
                <button className="px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded hover:bg-gray-200">
                  Setup
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 border border-gray-200 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 mb-1">2.4s</div>
            <div className="text-sm text-gray-600">Avg Page Load</div>
          </div>

          <div className="text-center p-4 border border-gray-200 rounded-lg">
            <div className="text-2xl font-bold text-green-600 mb-1">1,247</div>
            <div className="text-sm text-gray-600">Concurrent Users</div>
          </div>

          <div className="text-center p-4 border border-gray-200 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 mb-1">847ms</div>
            <div className="text-sm text-gray-600">API Response Time</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// System Settings Section
export const SystemSettings = ({ systemConfig, setSystemConfig }) => {
  const handleSaveConfig = async () => {
    try {
      toast.success('System configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  return (
    <div className="space-y-6">
      {/* General System Settings */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">General System Settings</h2>
        
        <div className="space-y-6">
          {/* Maintenance Mode */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">Maintenance Mode</h3>
              <p className="text-sm text-gray-600">Temporarily disable access for system maintenance</p>
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={systemConfig.maintenanceMode}
                onChange={(e) => setSystemConfig({
                  ...systemConfig,
                  maintenanceMode: e.target.checked
                })}
                className="rounded"
              />
              <span className="text-sm">
                {systemConfig.maintenanceMode ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>

          {/* Resource Limits */}
          <div>
            <h3 className="font-medium text-gray-900 mb-4">Global Resource Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Tenants Per Instance
                </label>
                <input
                  type="number"
                  value={systemConfig.globalResourceLimits.maxTenantsPerInstance}
                  onChange={(e) => setSystemConfig({
                    ...systemConfig,
                    globalResourceLimits: {
                      ...systemConfig.globalResourceLimits,
                      maxTenantsPerInstance: parseInt(e.target.value)
                    }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  min="1"
                  max="10000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Storage Limit (MB)
                </label>
                <input
                  type="number"
                  value={systemConfig.globalResourceLimits.maxStoragePerTenant}
                  onChange={(e) => setSystemConfig({
                    ...systemConfig,
                    globalResourceLimits: {
                      ...systemConfig.globalResourceLimits,
                      maxStoragePerTenant: parseInt(e.target.value)
                    }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  min="100"
                  max="100000"
                />
              </div>
            </div>
          </div>

          {/* Preview & Testing */}
          <div>
            <h3 className="font-medium text-gray-900 mb-4">Preview & Testing</h3>
            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Candidate Portal Preview</h4>
                    <p className="text-sm text-gray-600">Test the candidate self-service experience</p>
                  </div>
                  <a
                    href="/candidate/preview-candidate"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Open Portal</span>
                  </a>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Opens the candidate portal with sample data for testing the complete self-service experience including dashboard, profile, applications, interviews, and messaging.
                </div>
              </div>
              
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Scheduling Link Preview</h4>
                    <p className="text-sm text-gray-600">Test the candidate scheduling experience</p>
                  </div>
                  <a
                    href="/schedule/preview-token-123"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Test Scheduling</span>
                  </a>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Opens the candidate scheduling flow with mock available time slots for testing the self-service booking experience.
                </div>
              </div>
            </div>
          </div>

          {/* API Configuration */}
          <div>
            <h3 className="font-medium text-gray-900 mb-4">API Configuration</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate Limit (requests/minute)
                  </label>
                  <input
                    type="number"
                    defaultValue="1000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="10"
                    max="10000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    defaultValue="30"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    min="5"
                    max="300"
                  />
                </div>
              </div>

              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked className="rounded" />
                  <span className="text-sm">Enable API versioning</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked className="rounded" />
                  <span className="text-sm">API key authentication required</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">Enable CORS for all origins</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Environment Information */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Environment Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">System Info</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Platform Version:</span>
                <span className="font-medium">v2.1.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Environment:</span>
                <span className="font-medium text-green-600">Production</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Database:</span>
                <span className="font-medium">MongoDB 7.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Node.js:</span>
                <span className="font-medium">v20.11.0</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Security</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">SSL/TLS:</span>
                <span className="font-medium text-green-600">Enabled</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">HTTPS Redirect:</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">HSTS:</span>
                <span className="font-medium text-green-600">Enabled</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Security Headers:</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Configuration */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4 inline mr-2" />
          Reset
        </button>
        <button
          onClick={handleSaveConfig}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Save className="h-4 w-4 inline mr-2" />
          Save Configuration
        </button>
      </div>
    </div>
  );
};

export default {
  WorkflowTemplates,
  MonitoringAlerts,
  SystemSettings
};